export interface PortfolioFolder {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  access_level?: "public" | "private";
  share_hash?: string;
  share_hash_created_at?: string;
  public_url?: string;
  /** Cover image URL for Ready-made gallery cards. */
  banner?: string;
  /** Locale map, e.g. `{ en, uz, ru }`. */
  description?: Record<string, string>;
  /** Extra gallery image URLs (max 10). */
  galleries?: string[];
  /** When true, the folder appears in GET /portfolio/gallery. */
  show_in_gallery?: boolean;
  /** Present on public/shared broker folders. */
  source?: string;
  is_broker?: boolean;
  /** Ready-made portfolios: the screener query the holdings were last saved from. */
  screener_filter?: Record<string, string>;
  last_saved_holdings_at?: string;
  /** Users following this ready-made portfolio. */
  follower_count?: number;
  /** Time-weighted price return; absent until the nightly job has run. */
  performance?: PortfolioPerformanceSummary;
}

export interface PortfolioPerformanceSummary {
  return_all_pct: number;
  return_1m_pct: number;
  /** YYYY-MM-DD of the first point. */
  since: string;
}

export interface PortfolioFoldersPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface GetPortfolioFoldersQuery {
  page?: number;
  limit?: number;
}

export interface GetPortfolioFoldersResponse {
  success: boolean;
  data: PortfolioFolder[];
  pagination: PortfolioFoldersPagination;
  message: string;
  error: string;
}

export interface PortfolioFoldersErrorResponse {
  success: boolean;
  data: string;
  error: string;
  message: string;
}

export interface CreatePortfolioFolderRequest {
  name: string;
}

export interface CreatePortfolioFolderResponse {
  success: boolean;
  data: PortfolioFolder;
  message: string;
  error: string;
}

export interface UpdatePortfolioFolderRequest {
  id: string;
  name: string;
  banner?: string;
  description?: Record<string, string>;
  galleries?: string[];
  show_in_gallery?: boolean;
}

export interface SavePortfolioFolderPayload {
  name: string;
  banner?: string;
  description?: Record<string, string>;
  show_in_gallery?: boolean;
}

export interface GetPortfolioGalleryQuery {
  page?: number;
  limit?: number;
}

export interface GetPortfolioGalleryResponse {
  success: boolean;
  data: PortfolioFolder[];
  pagination: PortfolioFoldersPagination;
  message: string;
  error: string;
}

export interface GetPortfolioGalleryPreviewQuery {
  page?: number;
  limit?: number;
}

/** Stock fields returned by GET /portfolio/gallery/{id}. */
export interface GalleryPortfolioStock {
  id: string;
  user_id?: string;
  ticker?: string;
  company?: string;
  sector?: string;
  industry?: string;
  price?: number;
  quantity?: number;
  value?: number;
  current_price?: number;
  analyst_rating?: string;
  added_at?: string;
  created_at?: string;
  updated_at?: string;
  folder_id?: string;
  halal_status?: string;
  logoURL?: string;
  akinda_score?: number | string;
  changePercentage?: number;
}

export interface GetPortfolioGalleryPreviewResponse {
  success: boolean;
  data?: PortfolioFolder;
  folder?: PortfolioFolder;
  portfolios?: Array<GalleryPortfolioStock | PortfolioStock>;
  pagination?: PortfolioStocksPagination;
  message: string;
  error?: string;
}

const DESCRIPTION_LOCALES = ["en", "uz", "ru"] as const;

export const getLocalizedPortfolioDescription = (
  description: Record<string, string> | undefined,
  language: string,
): string => {
  if (!description) {
    return "";
  }

  const lang = language.split("-")[0];
  return (
    description[lang] ||
    description.en ||
    Object.values(description).find(Boolean) ||
    ""
  );
};

export const toPortfolioDescriptionMap = (
  text: string,
): Record<string, string> => {
  const value = text.trim();
  return DESCRIPTION_LOCALES.reduce<Record<string, string>>((acc, locale) => {
    acc[locale] = value;
    return acc;
  }, {});
};

export const unwrapPortfolioFolder = (
  response:
    | CreatePortfolioFolderResponse
    | UpdatePortfolioFolderResponse
    | GetPortfolioGalleryPreviewResponse
    | PortfolioFolder
    | undefined
    | null,
): PortfolioFolder | undefined => {
  if (!response) {
    return undefined;
  }

  if (
    "folder" in response &&
    response.folder &&
    typeof response.folder === "object" &&
    "id" in response.folder
  ) {
    return response.folder;
  }

  if (
    "data" in response &&
    response.data &&
    typeof response.data === "object" &&
    "id" in response.data
  ) {
    return response.data;
  }

  if ("id" in response && "name" in response) {
    return response;
  }

  return undefined;
};

export const toPortfolioStock = (
  raw: GalleryPortfolioStock | PortfolioStock,
): PortfolioStock => {
  const item = raw as GalleryPortfolioStock & Partial<PortfolioStock>;

  return {
    id: item.id,
    folder_id: item.folder_id ?? item.folder_id ?? "",
    ticker: item.ticker ?? item.ticker ?? "",
    company: item.company ?? item.company ?? "",
    sector: item.sector ?? "",
    industry: item.industry ?? "",
    analyst_rating: item.analyst_rating ?? item.analyst_rating ?? "",
    price: item.price ?? 0,
    current_price:
      item.current_price ?? item.current_price ?? item.price ?? 0,
    change: item.change ?? item.changePercentage ?? 0,
    quantity: item.quantity ?? item.quantity ?? 0,
    value: item.value ?? 0,
    target_price: item.target_price,
    halal_status: item.halal_status ?? item.halal_status,
    ranking: item.ranking ?? item.akinda_score,
    logoURL: item.logoURL ?? item.logoURL,
    added_at: item.added_at ?? item.added_at ?? item.created_at ?? "",
    created_at: item.created_at ?? "",
    updated_at: item.updated_at ?? "",
    user_id: item.user_id ?? item.user_id ?? "",
  };
};

export const unwrapGalleryHoldings = (
  response: GetPortfolioGalleryPreviewResponse | undefined,
): PortfolioStock[] => {
  const nestedPortfolios = (
    response?.folder as (PortfolioFolder & { portfolios?: unknown }) | undefined
  )?.portfolios;
  const rawPortfolios = Array.isArray(response?.portfolios)
    ? response.portfolios
    : Array.isArray(nestedPortfolios)
      ? nestedPortfolios
      : null;

  if (!rawPortfolios) {
    return [];
  }

  return rawPortfolios.map((item) =>
    toPortfolioStock(item as GalleryPortfolioStock | PortfolioStock),
  );
};

export interface UpdatePortfolioFolderResponse {
  success: boolean;
  data: PortfolioFolder;
  message: string;
  error: string;
}

export interface PortfolioStock {
  id: string;
  folder_id: string;
  ticker: string;
  company: string;
  sector: string;
  industry?: string;
  analyst_rating: string;
  price: number;
  current_price: number;
  change: number;
  quantity: number;
  value: number;
  target_price?: number;
  halal_status?: string;
  ranking?: number | string;
  /** Akinda ranking as sent by the API; the Ranking column reads this field. */
  akinda_score?: number | string;
  logoURL?: string;
  added_at: string;
  created_at: string;
  updated_at: string;
  user_id: string;
}

export interface PortfolioStocksPagination {
  limit: number;
  page: number;
  total: number;
  total_pages: number;
}

export interface GetPortfolioStocksQuery {
  page?: number;
  limit?: number;
  folder_id?: string;
  filter?: string;
  field?: string;
  /** Staff only: list another user's holdings (the folder owner). */
  userid?: string;
}

export interface GetPortfolioStocksResponse {
  success: boolean;
  data: PortfolioStock[];
  message: string;
  error: string;
  pagination: PortfolioStocksPagination;
}

export interface PlacePortfolioTradeRequest {
  folder_id: string;
  ticker: string;
  quantity: number;
}

export interface DemoAccount {
  folder_id: string;
  user_id: string;
  starting_cash: number;
  cash_balance: number;
  positions_value: number;
  equity: number;
  realized_pnl: number;
  unrealized_pnl: number;
  total_pnl: number;
  total_return_pct: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface DemoTrade {
  id: string;
  folder_id: string;
  user_id: string;
  ticker: string;
  side: "BUY" | "SELL" | string;
  quantity: number;
  price: number;
  amount: number;
  realized_pnl: number;
  cash_after: number;
  created_at: string;
  /** Staff member who placed it when acting on another user's folder. */
  actor_user_id?: string;
  /** Set on every trade of one "save holdings" call. */
  batch_id?: string;
}

export interface PlacePortfolioTradeResponse {
  success: boolean;
  data: {
    trade: DemoTrade;
    account: DemoAccount;
    position?: PortfolioStock;
  };
  message: string;
  error: string;
}

export interface GetDemoAccountResponse {
  success: boolean;
  data: DemoAccount;
  message: string;
  error: string;
}

export interface GetDemoTradesQuery {
  page?: number;
  limit?: number;
}

export interface GetDemoTradesResponse {
  success: boolean;
  data: DemoTrade[];
  message: string;
  error: string;
  pagination: PortfolioStocksPagination;
}

export interface PortfolioAnalyticsItem {
  name: string;
  total_value: number;
  percentage: number;
}

export interface PortfolioAnalyticsResponse {
  success: boolean;
  data: {
    industry_analitics: PortfolioAnalyticsItem[];
    sector_analitics: PortfolioAnalyticsItem[];
    total_portfolio_value: number;
    change_percentage: number;
    sum_wallet: number;
    today_profit_loss_amount: number;
    today_profit_loss_percentage: number;
  };
}

export interface UpdatePortfolioAccessRequest {
  id: string;
  access_level: "public" | "private";
}

export interface UpdatePortfolioAccessResponse {
  success: boolean;
  data: PortfolioFolder;
  message: string;
  error: string;
}

export interface GetPublicPortfolioQuery {
  page?: number;
  limit?: number;
}

export interface GetPublicPortfolioResponse {
  success: boolean;
  folder: PortfolioFolder;
  portfolios: PortfolioStock[];
  pagination: PortfolioStocksPagination;
}

// ---- Ready-made portfolios: save holdings from a screener filter ----

export interface HoldingTarget {
  ticker: string;
  /** Shares the folder must hold after Save. Always > 0. */
  quantity: number;
}

export interface SavePortfolioHoldingsRequest {
  /** Held tickers missing here are sold in full. */
  targets: HoldingTarget[];
  screener_filter?: Record<string, string>;
  /** Required when targets is empty (sells every holding). */
  confirm_empty?: boolean;
}

export type PlannedTradeReason = "added" | "removed" | "increased" | "decreased";

export interface PlannedTrade {
  ticker: string;
  side: "BUY" | "SELL" | string;
  quantity: number;
  price: number;
  amount: number;
  reason: PlannedTradeReason | string;
}

export interface SkippedTicker {
  ticker: string;
  reason: "unpriced" | string;
}

export interface SavePortfolioHoldingsResult {
  trades?: PlannedTrade[];
  skipped?: SkippedTicker[];
  account_before?: DemoAccount;
  account_after?: DemoAccount;
  dry_run?: boolean;
  batch_id?: string;
  /** > 0 when the buys cannot be funded (preview only; a real save fails). */
  cash_needed?: number;
}

export interface SavePortfolioHoldingsResponse {
  success: boolean;
  data: SavePortfolioHoldingsResult;
  message: string;
  error: string;
}

export interface DepositDemoCashRequest {
  folder_id: string;
  amount: number;
}

export interface DepositDemoCashResponse {
  success: boolean;
  data: {
    trade: DemoTrade;
    account: DemoAccount;
  };
  message: string;
  error: string;
}

// ---- Ready-made portfolios: follow and public trade history ----

export interface PortfolioFollowState {
  following: boolean;
  follower_count: number;
}

export interface PortfolioFollowStateResponse {
  success: boolean;
  data: PortfolioFollowState;
  message?: string;
  error?: string;
}

/** One row of a ready-made portfolio's public trade history. */
export interface GalleryTrade {
  id: string;
  side: "BUY" | "SELL" | "DEPOSIT" | string;
  ticker: string;
  quantity: number;
  price: number;
  amount: number;
  created_at: string;
}

export interface GalleryTradesQuery {
  page?: number;
  limit?: number;
}

export interface GalleryTradesResponse {
  success: boolean;
  data: GalleryTrade[];
  pagination: PortfolioStocksPagination;
  message?: string;
  error?: string;
}

// ---- Ready-made portfolios: performance chart ----

export type PortfolioPerformanceRange = "1w" | "1m" | "3m" | "6m" | "all";

export interface PortfolioPerformancePoint {
  date: string;
  index: number;
  /** Relative to the window's first point. */
  return_pct: number;
}

export interface PortfolioPerformance {
  range: PortfolioPerformanceRange;
  start_date: string;
  end_date: string;
  return_pct: number;
  growth_of_1000: number;
  price_return: boolean;
  has_gaps: boolean;
  points: PortfolioPerformancePoint[];
}

export interface PortfolioPerformanceResponse {
  success: boolean;
  data: PortfolioPerformance;
  message?: string;
  error?: string;
}
